import React, { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { finalizeProductImageFile, createUniqueFileName } from '../../utils/imageCompression';
import { getPriceInputLabel } from '../../utils/orderDisplay';
import { createEmptyVariant } from '../../utils/productVariants';
import ImageCropperModal from '../Common/ImageCropperModal/ImageCropperModal';

const MAX_VARIANT_MEDIA = 5;
const PRODUCT_IMAGE_ASPECT = 3 / 4;

const ProductVariantsEditor = ({ variants = [], onChange, disabled = false, basePrice = '', adminData }) => {
  const [processingVariantIndex, setProcessingVariantIndex] = useState(null);
  const [cropQueue, setCropQueue] = useState(null);

  const updateVariants = (nextVariants) => onChange?.(nextVariants);

  const handleAddVariant = () => {
    updateVariants([...variants, createEmptyVariant(basePrice)]);
  };

  const handleRemoveVariant = (index) => {
    updateVariants(variants.filter((_, i) => i !== index));
  };

  const handleVariantChange = (index, field, value) => {
    const next = [...variants];
    next[index] = { ...next[index], [field]: value };
    updateVariants(next);
  };

  const handleAddAttribute = (vIndex) => {
    const next = [...variants];
    next[vIndex] = {
      ...next[vIndex],
      attributes: [...(next[vIndex].attributes || []), { option_name: 'size', option_value: '' }],
    };
    updateVariants(next);
  };

  const handleRemoveAttribute = (vIndex, aIndex) => {
    const next = [...variants];
    next[vIndex] = {
      ...next[vIndex],
      attributes: next[vIndex].attributes.filter((_, i) => i !== aIndex),
    };
    updateVariants(next);
  };

  const handleAttributeChange = (vIndex, aIndex, field, value) => {
    const next = [...variants];
    const attributes = [...next[vIndex].attributes];
    attributes[aIndex] = { ...attributes[aIndex], [field]: value };
    next[vIndex] = { ...next[vIndex], attributes };
    updateVariants(next);
  };

  const openNextCrop = (files, vIndex) => {
    const [first, ...rest] = files;
    if (!first) {
      setCropQueue(null);
      setProcessingVariantIndex(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = (readerEvent) => {
      setCropQueue({
        src: readerEvent.target.result,
        vIndex,
        remaining: rest,
      });
    };
    reader.readAsDataURL(first);
  };

  const handleVariantMediaSelect = (e, vIndex) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length || cropQueue) return;

    const currentCount = variants[vIndex]?.media?.length || 0;
    if (currentCount + files.length > MAX_VARIANT_MEDIA) {
      toast.error(`Maximum ${MAX_VARIANT_MEDIA} images allowed per variant`);
      return;
    }

    const invalid = files.find((file) => !file.type?.startsWith('image/') || file.size > 8 * 1024 * 1024);
    if (invalid && !invalid.type?.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }
    if (invalid) {
      toast.error('Each source image must be less than 8MB');
      return;
    }

    setProcessingVariantIndex(vIndex);
    openNextCrop(files, vIndex);
  };

  const appendCroppedMedia = async (croppedFile, vIndex) => {
    const finalImage = await finalizeProductImageFile(croppedFile);
    const namedImage = new File(
      [finalImage],
      createUniqueFileName(finalImage.name || 'variant.jpg'),
      { type: finalImage.type || 'image/jpeg', lastModified: Date.now() }
    );
    const previewUrl = URL.createObjectURL(namedImage);
    const next = [...variants];
    const current = next[vIndex]?.media || [];
    next[vIndex] = {
      ...next[vIndex],
      media: [
        ...current,
        {
          type: 'new',
          mediaType: 'image',
          url: previewUrl,
          thumbnail: previewUrl,
          file: namedImage,
          order: String(current.length + 1),
        },
      ],
    };
    updateVariants(next);
  };

  const handleVariantCropComplete = async (croppedFile) => {
    if (!cropQueue) return;
    const { vIndex, remaining } = cropQueue;
    setCropQueue(null);
    try {
      await appendCroppedMedia(croppedFile, vIndex);
      if (remaining.length) {
        openNextCrop(remaining, vIndex);
      } else {
        setProcessingVariantIndex(null);
      }
    } catch (err) {
      setProcessingVariantIndex(null);
      toast.error(err.message || 'Failed to process variant image');
    }
  };

  const handleVariantCropClose = () => {
    setCropQueue(null);
    setProcessingVariantIndex(null);
  };

  const handleRemoveVariantMedia = (vIndex, mIndex) => {
    const next = [...variants];
    const mediaItem = next[vIndex].media[mIndex];
    if (mediaItem?.url?.startsWith('blob:')) {
      URL.revokeObjectURL(mediaItem.url);
    }
    next[vIndex] = {
      ...next[vIndex],
      media: next[vIndex].media.filter((_, i) => i !== mIndex),
    };
    updateVariants(next);
  };

  return (
    <div className="productlist-variants-section">
      <span className="productlist-section-label">Product Variants</span>

      {variants.map((variant, vIdx) => (
        <div key={`variant-${vIdx}`} className="productlist-variant-card">
          <div className="productlist-variant-card-header">
            <h4>Variant No. {vIdx + 1}</h4>
            {!disabled && (
              <button
                type="button"
                className="productlist-variant-remove-btn"
                onClick={() => handleRemoveVariant(vIdx)}
              >
                <Trash2 size={18} /> Remove
              </button>
            )}
          </div>

          <div className="productlist-variant-row-fields">
            <div className="productlist-form-group productlist-variant-price-group">
              <label>{getPriceInputLabel(adminData)} *</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={variant.price}
                onChange={(e) => handleVariantChange(vIdx, 'price', e.target.value)}
                placeholder="Price"
                disabled={disabled}
              />
            </div>
            <div className="productlist-variant-status-item">
              <label>Status</label>
              <div className="availability-toggle-container">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={variant.isAvailable !== false}
                    onChange={(e) => handleVariantChange(vIdx, 'isAvailable', e.target.checked)}
                    disabled={disabled}
                  />
                  <span className="toggle-slider" />
                </label>
                <span className={`availability-text ${variant.isAvailable !== false ? 'in-stock' : 'out-of-stock'}`}>
                  {variant.isAvailable !== false ? 'In Stock' : 'Out of Stock'}
                </span>
              </div>
            </div>
          </div>

          <div className="productlist-variant-attributes-section">
            <div className="productlist-variant-attributes-header">
              <div className="productlist-attr-header-label">Attribute Key</div>
              <div className="productlist-attr-header-label">Attribute Value</div>
            </div>
            {(variant.attributes || []).map((attr, aIdx) => (
              <div key={`attr-${vIdx}-${aIdx}`} className="productlist-attribute-row">
                <div className="productlist-attr-field-group">
                  <select
                    value={attr.option_name}
                    onChange={(e) => handleAttributeChange(vIdx, aIdx, 'option_name', e.target.value)}
                    disabled={disabled}
                  >
                    <option value="color">Color</option>
                    <option value="size">Size</option>
                    <option value="weight">Weight</option>
                  </select>
                </div>
                <div className="productlist-attr-field-group">
                  <input
                    type="text"
                    value={attr.option_value}
                    onChange={(e) => handleAttributeChange(vIdx, aIdx, 'option_value', e.target.value)}
                    placeholder="e.g. Red, Large"
                    disabled={disabled}
                  />
                </div>
                {!disabled && (
                  <button
                    type="button"
                    className="productlist-attr-remove-btn"
                    onClick={() => handleRemoveAttribute(vIdx, aIdx)}
                    title="Remove attribute"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            {!disabled && (
              <div className="productlist-add-attr-container">
                <button
                  type="button"
                  className="productlist-add-attr-btn"
                  onClick={() => handleAddAttribute(vIdx)}
                >
                  <Plus size={16} /> Add Attribute
                </button>
              </div>
            )}
          </div>

          <div className="productlist-variant-media-section">
            <label>Variant Media (3:4)</label>
            <div className="productlist-variant-media-grid">
              {!disabled && (
                <label className={`productlist-variant-media-add ${processingVariantIndex === vIdx ? 'loading' : ''}`}>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => handleVariantMediaSelect(e, vIdx)}
                    style={{ display: 'none' }}
                    disabled={disabled || processingVariantIndex === vIdx}
                  />
                  {processingVariantIndex === vIdx ? '...' : <Plus size={20} />}
                </label>
              )}
              {(variant.media || []).map((media, mIdx) => (
                <div key={`media-${vIdx}-${mIdx}`} className="productlist-variant-media-item">
                  <img src={media.thumbnail || media.url} alt="Variant" />
                  {!disabled && (
                    <button
                      type="button"
                      className="productlist-variant-media-remove"
                      onClick={() => handleRemoveVariantMedia(vIdx, mIdx)}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}

      {!disabled && (
        <div className="productlist-add-variant-container">
          <button type="button" className="productlist-add-variant-btn" onClick={handleAddVariant}>
            <Plus size={18} /> Add New Variant
          </button>
        </div>
      )}
      {cropQueue?.src && (
        <ImageCropperModal
          imageSrc={cropQueue.src}
          aspectRatio={PRODUCT_IMAGE_ASPECT}
          title="Crop variant image (3:4)"
          onClose={handleVariantCropClose}
          onCropComplete={handleVariantCropComplete}
        />
      )}
    </div>
  );
};

export default ProductVariantsEditor;
